/**
 * Account Settings Tab
 * Manages personal user account settings (name)
 */
import { useState, useEffect } from 'react';
import { useAuth } from '@/lib/auth/AuthProvider';
import { useUpdateProfile } from '@/hooks';
import { Button } from '@/components/ui/button';
import { User, Save } from 'lucide-react';

export function AccountSettingsTab() {
  const { user } = useAuth();
  const updateProfile = useUpdateProfile();

  const [name, setName] = useState('');

  // Initialize form with current user data
  useEffect(() => {
    if (user?.name) {
      setName(user.name);
    }
  }, [user?.name]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) return;
    await updateProfile.mutateAsync({ name: name.trim() });
  };

  const hasChanges = name.trim() !== (user?.name || '');

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-3 pb-4 border-b">
        <User className="w-5 h-5 text-slate-600" />
        <div>
          <h2 className="text-lg font-semibold text-gray-900">Account Settings</h2>
          <p className="text-sm text-gray-600 mt-1">
            Manage your personal account information
          </p>
        </div>
      </div>

      {/* Form Fields */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Display Name */}
        <div className="md:col-span-2">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Display Name <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
            placeholder="Your name"
          />
          <p className="text-xs text-gray-500 mt-1">
            This is how your name appears throughout the application
          </p>
        </div>

        {/* Email (read-only) */}
        <div className="md:col-span-2">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Email Address
          </label>
          <input
            type="email"
            value={user?.email || ''}
            disabled
            className="w-full px-3 py-2 border border-gray-200 rounded-lg bg-gray-50 text-gray-500 cursor-not-allowed"
          />
          <p className="text-xs text-gray-500 mt-1">
            Your email address cannot be changed
          </p>
        </div>
      </div>

      {/* Submit Button */}
      <div className="flex items-center justify-end gap-3 pt-4 border-t">
        <Button
          type="submit"
          disabled={updateProfile.isPending || !name.trim() || !hasChanges}
          className="inline-flex items-center gap-2"
        >
          <Save className="w-4 h-4" />
          {updateProfile.isPending ? 'Saving...' : 'Save Changes'}
        </Button>
      </div>
    </form>
  );
}
